// FinKloz — Bank Reconciliation State Manager
// useReducer for state + Supabase as primary persistence.

import { useReducer, useEffect, useCallback, useRef } from 'react';
import { BANK_ACCOUNTS } from '../services/bankStatementParser';
import { getAccessTokenSync } from './useAccessToken';

const SB_URL = process.env.REACT_APP_SUPABASE_URL;
const SB_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

function sbHeaders() {
  const token = getAccessTokenSync();
  return {
    'apikey': SB_KEY,
    'Authorization': `Bearer ${token || SB_KEY}`,
    'Content-Type': 'application/json',
    'Prefer': 'return=representation',
  };
}

async function sbFetch(path, options = {}) {
  const resp = await fetch(`${SB_URL}/rest/v1/${path}`, {
    ...options,
    headers: { ...sbHeaders(), ...(options.headers || {}) },
  });
  if (!resp.ok) {
    const text = await resp.text();
    void(`Supabase ${path} failed:`, resp.status, text);
    return null;
  }
  return resp.json();
}

// ─── Initial state ───
function buildInitialState(month) {
  const accounts = {};
  for (const acct of BANK_ACCOUNTS) {
    accounts[acct.accountNo] = {
      status: 'not_started',
      statement: null,
      glData: null,
      matchResult: null,
      reconcilingItems: [],
      reconciliation: null,
      proposedJEs: [],
      fxConversion: null,
      updatedAt: null,
    };
  }
  return {
    month: month || '',
    activeAccount: BANK_ACCOUNTS[0]?.accountNo || null,
    accounts,
    templatePopulated: false,
    _loaded: false,
  };
}

// ─── Reducer (same as before) ───
function bankRecReducer(state, action) {
  switch (action.type) {
    case 'RESET_MONTH':
      return buildInitialState(action.month);

    case 'SET_ACTIVE_ACCOUNT':
      return { ...state, activeAccount: action.accountNo };

    case 'STATEMENT_UPLOADED': {
      const { accountNo, statement } = action;
      const acct = state.accounts[accountNo] || {};
      const hasGL = acct.glData;
      return {
        ...state,
        accounts: {
          ...state.accounts,
          [accountNo]: { ...acct, status: hasGL ? 'ready_to_reconcile' : 'statement_uploaded', statement, updatedAt: new Date().toISOString() },
        },
      };
    }

    case 'GL_UPLOADED': {
      const { accountNo, glData } = action;
      const acct = state.accounts[accountNo] || {};
      const hasStmt = acct.statement;
      return {
        ...state,
        accounts: {
          ...state.accounts,
          [accountNo]: { ...acct, status: hasStmt ? 'ready_to_reconcile' : 'gl_uploaded', glData, updatedAt: new Date().toISOString() },
        },
      };
    }

    case 'RECONCILIATION_RUN': {
      const { accountNo, matchResult, reconcilingItems, reconciliation, proposedJEs } = action;
      const acct = state.accounts[accountNo] || {};
      return {
        ...state,
        accounts: {
          ...state.accounts,
          [accountNo]: {
            ...acct, status: 'matched', matchResult, reconcilingItems, reconciliation, proposedJEs,
            updatedAt: new Date().toISOString(),
          },
        },
      };
    }

    case 'UPDATE_RECONCILING_ITEM': {
      const { accountNo, itemId, updates } = action;
      const acct = state.accounts[accountNo] || {};
      return {
        ...state,
        accounts: { ...state.accounts, [accountNo]: {
          ...acct,
          reconcilingItems: (acct.reconcilingItems || []).map(item => item.id === itemId ? { ...item, ...updates } : item),
          updatedAt: new Date().toISOString(),
        }},
      };
    }

    case 'APPROVE_JE': {
      const { accountNo, jeId, approved } = action;
      const acct = state.accounts[accountNo] || {};
      return {
        ...state,
        accounts: { ...state.accounts, [accountNo]: {
          ...acct,
          proposedJEs: (acct.proposedJEs || []).map(je => je.id === jeId ? { ...je, approved } : je),
          updatedAt: new Date().toISOString(),
        }},
      };
    }

    case 'APPROVE_ALL_JES': {
      const { accountNo } = action;
      const acct = state.accounts[accountNo] || {};
      return {
        ...state,
        accounts: { ...state.accounts, [accountNo]: {
          ...acct,
          proposedJEs: (acct.proposedJEs || []).map(je => ({ ...je, approved: true })),
          updatedAt: new Date().toISOString(),
        }},
      };
    }

    case 'SET_FX_CONVERSION': {
      const { accountNo, fxConversion } = action;
      const acct = state.accounts[accountNo] || {};
      return {
        ...state,
        accounts: { ...state.accounts, [accountNo]: { ...acct, fxConversion, updatedAt: new Date().toISOString() }},
      };
    }

    case 'MARK_REVIEWED': {
      const { accountNo } = action;
      const acct = state.accounts[accountNo] || {};
      return {
        ...state,
        accounts: { ...state.accounts, [accountNo]: { ...acct, status: 'reviewed', updatedAt: new Date().toISOString() }},
      };
    }

    case 'MARK_COMPLETED': {
      const { accountNo } = action;
      const acct = state.accounts[accountNo] || {};
      return {
        ...state,
        accounts: { ...state.accounts, [accountNo]: { ...acct, status: 'completed', updatedAt: new Date().toISOString() }},
      };
    }

    case 'FLIP_SIGN': {
      const { accountNo, txnId, source } = action;
      const acct = state.accounts[accountNo] || {};
      if (source === 'bank' && acct.statement) {
        return {
          ...state,
          accounts: { ...state.accounts, [accountNo]: {
            ...acct,
            statement: { ...acct.statement, transactions: acct.statement.transactions.map(t => t.id === txnId ? { ...t, debit: t.credit, credit: t.debit } : t) },
          }},
        };
      }
      if (source === 'gl' && acct.glData) {
        return {
          ...state,
          accounts: { ...state.accounts, [accountNo]: {
            ...acct,
            glData: { ...acct.glData, entries: acct.glData.entries.map(t => t.id === txnId ? { ...t, debit: t.credit, credit: t.debit } : t) },
          }},
        };
      }
      return state;
    }

    case 'ADD_PROPOSED_JE': {
      const { accountNo, je } = action;
      const acct = state.accounts[accountNo] || {};
      return {
        ...state,
        accounts: { ...state.accounts, [accountNo]: {
          ...acct, proposedJEs: [...(acct.proposedJEs || []), je], updatedAt: new Date().toISOString(),
        }},
      };
    }

    case 'MANUAL_MATCH': {
      const { accountNo, bankItems, glItems } = action;
      const acct = state.accounts[accountNo] || {};
      const mr = acct.matchResult || { matched: [], unmatchedBank: [], unmatchedGL: [], stats: {} };
      const bankIds = new Set(bankItems.map(b => b.id));
      const glIds = new Set(glItems.map(g => g.id));
      const newEntries = [];
      if (bankItems.length === 1 && glItems.length >= 1) {
        newEntries.push({ bankTxn: bankItems[0], glEntries: glItems, confidence: 'manual', matchType: 'manual' });
      } else if (glItems.length === 1 && bankItems.length >= 1) {
        newEntries.push({ bankTxns: bankItems, glEntry: glItems[0], confidence: 'manual', matchType: 'manual' });
      } else {
        bankItems.forEach((b, i) => {
          newEntries.push({ bankTxn: b, glEntry: glItems[i] || glItems[0], confidence: 'manual', matchType: 'manual' });
        });
      }
      const newMatched = [...mr.matched, ...newEntries];
      const newUnmatchedBank = mr.unmatchedBank.filter(b => !bankIds.has(b.id));
      const newUnmatchedGL = mr.unmatchedGL.filter(g => !glIds.has(g.id));
      const totalBank = mr.stats.bankCount || 0;
      return {
        ...state,
        accounts: { ...state.accounts, [accountNo]: {
          ...acct,
          matchResult: {
            matched: newMatched, unmatchedBank: newUnmatchedBank, unmatchedGL: newUnmatchedGL,
            stats: { ...mr.stats, matchedCount: newMatched.length, matchRate: totalBank > 0 ? Math.round(newMatched.length / totalBank * 100) : 0 },
          },
          updatedAt: new Date().toISOString(),
        }},
      };
    }

    case 'RESET_ACCOUNT': {
      const { accountNo, companyId, month } = action;
      // Delete from Supabase too
      if (SB_URL && SB_KEY && companyId && month) {
        sbFetch(`bank_statements?company_id=eq.${companyId}&account_no=eq.${accountNo}&month=eq.${month}`, { method: 'DELETE' });
        sbFetch(`bank_reconciliations?company_id=eq.${companyId}&account_no=eq.${accountNo}&month=eq.${month}`, { method: 'DELETE' });
      }
      return {
        ...state,
        accounts: { ...state.accounts, [accountNo]: {
          status: 'not_started', statement: null, glData: null, matchResult: null,
          reconcilingItems: [], reconciliation: null, proposedJEs: [], fxConversion: null, updatedAt: null,
        }},
      };
    }

    case 'LOAD_FROM_SUPABASE': {
      const { statements, reconciliations } = action;
      const newAccounts = { ...state.accounts };

      // Load statements
      for (const row of (statements || [])) {
        const acctNo = row.account_no;
        if (!newAccounts[acctNo]) continue;
        newAccounts[acctNo] = {
          ...newAccounts[acctNo],
          statement: {
            file: { name: row.filename || 'bank statement', uploadedAt: row.created_at },
            metadata: {
              bankName: row.bank_name,
              closingBalance: parseFloat(row.closing_balance) || 0,
              openingBalance: parseFloat(row.opening_balance) || 0,
              currency: row.currency || 'CAD',
              periodEnd: null,
              periodStart: null,
            },
            transactions: row.parsed_data?.transactions || [],
          },
          status: newAccounts[acctNo].glData ? 'ready_to_reconcile' : 'statement_uploaded',
        };
      }

      // Load reconciliations (includes GL data, match results, everything)
      for (const row of (reconciliations || [])) {
        const acctNo = row.account_no;
        if (!newAccounts[acctNo]) continue;
        const rd = row.result_data || {};
        newAccounts[acctNo] = {
          ...newAccounts[acctNo],
          status: row.status || newAccounts[acctNo].status,
          glData: rd.glData || newAccounts[acctNo].glData,
          matchResult: rd.matchResult || newAccounts[acctNo].matchResult,
          reconcilingItems: rd.reconcilingItems || [],
          reconciliation: rd.reconciliation || null,
          proposedJEs: rd.proposedJEs || [],
          fxConversion: rd.fxConversion || null,
          updatedAt: row.run_at || row.created_at,
        };
      }

      return { ...state, accounts: newAccounts, _loaded: true };
    }

    case 'SET_TEMPLATE_POPULATED':
      return { ...state, templatePopulated: action.value };

    default:
      return state;
  }
}

// ─── Supabase save helpers ───
async function saveStatement(companyId, month, accountNo, statementData) {
  if (!SB_URL || !SB_KEY || !companyId) return;
  const body = {
    company_id: companyId,
    account_no: accountNo,
    month,
    filename: statementData.file?.name || '',
    bank_name: statementData.metadata?.bankName || '',
    opening_balance: statementData.metadata?.openingBalance || 0,
    closing_balance: statementData.metadata?.closingBalance || 0,
    currency: statementData.metadata?.currency || 'CAD',
    total_transactions: statementData.transactions?.length || 0,
    parsed_data: { transactions: statementData.transactions || [] },
    parsed_at: new Date().toISOString(),
  };
  await sbFetch('bank_statements?on_conflict=company_id,account_no,month', {
    method: 'POST',
    headers: { 'Prefer': 'resolution=merge-duplicates,return=representation' },
    body: JSON.stringify(body),
  });
}

async function saveReconciliation(companyId, month, accountNo, accountData) {
  if (!SB_URL || !SB_KEY || !companyId) return;
  const rec = accountData.reconciliation || {};
  const body = {
    company_id: companyId,
    account_no: accountNo,
    month,
    bank_balance: rec.bankBalance || 0,
    gl_balance: rec.glBalance || 0,
    adjusted_bank: rec.adjustedBankBalance || 0,
    adjusted_book: rec.adjustedBookBalance || 0,
    difference: rec.difference || 0,
    status: accountData.status || 'draft',
    match_rate: accountData.matchResult?.stats?.matchRate || 0,
    result_data: {
      glData: accountData.glData ? {
        source: accountData.glData.source,
        file: accountData.glData.file,
        entries: accountData.glData.entries,
        endingBalance: accountData.glData.endingBalance,
        totalDebit: accountData.glData.totalDebit,
        totalCredit: accountData.glData.totalCredit,
      } : null,
      matchResult: accountData.matchResult,
      reconcilingItems: accountData.reconcilingItems,
      reconciliation: accountData.reconciliation,
      proposedJEs: accountData.proposedJEs,
      fxConversion: accountData.fxConversion,
    },
    run_at: new Date().toISOString(),
  };
  await sbFetch('bank_reconciliations?on_conflict=company_id,account_no,month', {
    method: 'POST',
    headers: { 'Prefer': 'resolution=merge-duplicates,return=representation' },
    body: JSON.stringify(body),
  });
}

// ─── Main hook ───
export default function useBankRecState(companyId, month) {
  const [state, dispatch] = useReducer(bankRecReducer, month, buildInitialState);
  const saveTimerRef = useRef(null);
  const prevStateRef = useRef(null);

  // Reset and reload when month changes
  useEffect(() => {
    dispatch({ type: 'RESET_MONTH', month });
    if (!SB_URL || !SB_KEY || !companyId || !month) return;

    async function load() {
      const [statements, reconciliations] = await Promise.all([
        sbFetch(`bank_statements?company_id=eq.${companyId}&month=eq.${month}&select=*`),
        sbFetch(`bank_reconciliations?company_id=eq.${companyId}&month=eq.${month}&select=*`),
      ]);
      if (statements || reconciliations) {
        dispatch({ type: 'LOAD_FROM_SUPABASE', statements: statements || [], reconciliations: reconciliations || [] });
      }
    }

    load();
  }, [companyId, month]);

  // Auto-save to Supabase on state changes (debounced 2s)
  useEffect(() => {
    if (!companyId || !month) return;

    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      // Save any account that has data
      for (const [acctNo, acct] of Object.entries(state.accounts)) {
        if (acct.status === 'not_started') continue;

        const prev = prevStateRef.current?.accounts?.[acctNo];
        if (prev && acct === prev) continue; // reference equality = no change

        // Save statement if present and changed
        if (acct.statement && (!prev || acct.statement !== prev.statement)) {
          saveStatement(companyId, month, acctNo, acct.statement);
        }

        // Save reconciliation/status if changed
        if (acct.updatedAt && (!prev || acct.updatedAt !== prev.updatedAt)) {
          saveReconciliation(companyId, month, acctNo, acct);
        }
      }

      prevStateRef.current = state;
    }, 1500);

    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, [state, companyId, month]);

  // Force-save a specific account immediately (no debounce)
  const saveNow = useCallback((accountNo) => {
    if (!companyId || !month) return;
    const acct = state.accounts[accountNo];
    if (!acct || acct.status === 'not_started') return;
    if (acct.statement) saveStatement(companyId, month, accountNo, acct.statement);
    saveReconciliation(companyId, month, accountNo, acct);
    prevStateRef.current = state;
  }, [state, companyId, month]);

  return { state, dispatch, saveNow };
}
